import React, { useState, useEffect, useRef } from 'react';

// GOV13 — Enterprise / on-request commercial-review request modal. REQUEST_REQUIRED base plans (Enterprise) are
// never self-serve assigned and have NO direct Checkout: clicking "Request review" opens this form, which submits a
// canonical tenant-scoped COMMERCIAL_REVIEW request through the authenticated Business API. It creates NO Checkout,
// assigns NO plan, and moves no money. It shows truthful loading / success / validation / API-error states, surfaces
// an already-open request instead of duplicating, and is keyboard accessible (Esc closes, focus trapped to the form).
export default function EnterpriseReviewModal({ env, defaultEmail = '', submitting = false, result = null,
                                               onSubmit, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(defaultEmail || '');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const firstRef = useRef(null);

  useEffect(() => { if (firstRef.current) firstRef.current.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const nameValid = name.trim().length > 0;
  const canSubmit = nameValid && emailValid && !submitting;
  const productLabel = `${env?.display_name || env?.product_key || 'Enterprise'}${env?.product_version ? ` / ${env.product_version}` : ''}`;

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit && onSubmit({ product_key: env?.product_key, contact_name: name.trim(), contact_email: email.trim(),
      contact_phone: phone.trim() || undefined, message: message.trim() || undefined });
  };

  const existing = result && result.existing;
  const succeeded = result && result.created;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true"
      aria-label="Request Enterprise commercial review" data-testid="enterprise-review-modal">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Request commercial review</h3>
          <button type="button" onClick={() => onClose && onClose()} disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Close" data-testid="review-close-btn">×</button>
        </div>

        {succeeded || existing ? (
          <div className="px-4 py-5">
            <div className={`text-sm ${existing ? 'text-amber-700' : 'text-green-700'}`}
              data-testid={existing ? 'review-existing' : 'review-success'}>
              {existing
                ? 'You already have an open Enterprise review request. Our commercial team will contact you.'
                : 'Your Enterprise review request has been submitted. Our commercial team will contact you.'}
            </div>
            {result?.request?.status && (
              <div className="mt-2 text-xs text-gray-500" data-testid="review-status">Status: {result.request.status}</div>
            )}
            <div className="mt-4 text-right">
              <button type="button" onClick={() => onClose && onClose()}
                className="px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700" data-testid="review-done-btn">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-4 py-4 space-y-3" noValidate>
            <p className="text-xs text-gray-500" data-testid="review-product">
              Product: <span className="font-medium text-gray-800">{productLabel}</span> — priced on request; no automatic entitlement, no checkout.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Contact name<span className="text-red-500">*</span></label>
              <input ref={firstRef} type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="review-contact-name" />
              {touched && !nameValid && <p className="text-[11px] text-red-600 mt-0.5" data-testid="review-name-error">Contact name is required.</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Business email<span className="text-red-500">*</span></label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="review-contact-email" />
              {touched && !emailValid && <p className="text-[11px] text-red-600 mt-0.5" data-testid="review-email-error">A valid business email is required.</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Phone <span className="text-gray-400">(optional)</span></label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="review-contact-phone" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Requirements <span className="text-gray-400">(optional)</span></label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="review-message" />
            </div>
            {result && result.error && (
              <p className="text-xs text-red-600" data-testid="review-error">{result.error}</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => onClose && onClose()} disabled={submitting}
                className="px-3 py-1.5 text-sm rounded border disabled:opacity-50" data-testid="review-cancel-btn">Cancel</button>
              <button type="submit" disabled={!canSubmit}
                className="px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                data-testid="review-submit-btn">{submitting ? 'Submitting…' : 'Submit request'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
